import { useMemo } from 'react';

const EMBED_PATH_PREFIX = '/embed/';
const PUBLIC_DIAGRAM_PATH_PREFIX = '/public/diagrams/';

export function isEmbedPath(pathname) {
  if (typeof pathname !== 'string') return false;
  return pathname.startsWith(EMBED_PATH_PREFIX);
}

// The UK cost calculator is built for narrow frames (C7): a phone, or a 360px
// sidebar iframe on a desktop page — where matchMedia reads the FRAME's width.
export const UK_CALCULATOR_EMBED_PATH = '/embed/uk-cost-calculator';

export function isUkCalculatorEmbedPath(pathname) {
  return pathname === UK_CALCULATOR_EMBED_PATH;
}

export function isPublicDiagramPath(pathname) {
  if (typeof pathname !== 'string') return false;
  return pathname.startsWith(PUBLIC_DIAGRAM_PATH_PREFIX);
}

function readSearchParam(params, key) {
  const raw = params.get(key);
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

function readNumberParam(params, key) {
  const raw = readSearchParam(params, key);
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function readBoolParam(params, key) {
  const raw = readSearchParam(params, key);
  if (raw === null) return false;
  return raw === '1' || raw.toLowerCase() === 'true' || raw.toLowerCase() === 'yes';
}

export function parseEmbedConfig(search, pathname) {
  const params = new URLSearchParams(search || '');
  const isEmbed = isEmbedPath(pathname);
  const embedMode = isEmbed ? 'default-costs' : null;

  return {
    isEmbed,
    embedMode,
    partnerKey: readSearchParam(params, 'key'),
    initial: {
      cnGood: readSearchParam(params, 'cnGood'),
      carbonPrice: readNumberParam(params, 'carbonPrice'),
      year: readNumberParam(params, 'year'),
    },
    parentOrigin: readSearchParam(params, 'parentOrigin'),
    debug: readBoolParam(params, 'debug'),
    // Partner attribution badge. Default ON; enterprise partners can opt out
    // via ?hidePoweredBy=1 (and the backend gates this server-side per key in
    // a follow-up so partners can't unilaterally hide attribution).
    hidePoweredBy: readBoolParam(params, 'hidePoweredBy'),
  };
}

export function useEmbedConfig() {
  return useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        isEmbed: false,
        embedMode: null,
        partnerKey: null,
        initial: {},
        parentOrigin: null,
        debug: false,
        hidePoweredBy: false,
      };
    }
    return parseEmbedConfig(window.location.search, window.location.pathname);
  }, []);
}
